import { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/apiClient";
import { usePark } from "@/contexts/ParkContext";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { PageHeader, formatCurrency, formatDate } from "@/components/shared";
import { FileDown, Printer } from "lucide-react";
import { toast } from "sonner";

const todayIso = () => new Date().toISOString().slice(0, 10);
const ymIso = () => new Date().toISOString().slice(0, 7);
const daysAgoIso = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};
const monthStart = (ym) => `${ym}-01`;
const monthEnd = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(Date.UTC(y, m, 0));
  return d.toISOString().slice(0, 10);
};

// Build export URL that includes X-Park-Id via query (since browser direct
// nav won't send the header). The PDF/CSV endpoints accept ?park_id as fallback.
const exportUrl = (path, parkId) => {
  const url = new URL(`${API}${path}`);
  if (!url.searchParams.has("park_id")) url.searchParams.set("park_id", parkId);
  return url.toString();
};

function Stat({ label, value, sub, testId }) {
  return (
    <Card className="border-[#EAEAE5] shadow-none" data-testid={testId}>
      <CardContent className="p-5">
        <div className="text-[10px] uppercase tracking-widest text-[#737A76] mb-2">{label}</div>
        <div className="text-3xl font-semibold tracking-tight text-[#1A211D]" style={{ fontFamily: "Outfit" }}>{value}</div>
        {sub && <div className="text-xs text-[#737A76] mt-1">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function ExportButtons({ pdfHref, csvHref, label = "Export", testIdPrefix }) {
  return (
    <div className="flex items-center gap-2">
      {pdfHref && (
        <a href={pdfHref} target="_blank" rel="noreferrer" data-testid={`${testIdPrefix}-pdf-link`}>
          <Button size="sm" variant="outline" className="gap-1.5"><Printer size={14} /> PDF</Button>
        </a>
      )}
      {csvHref && (
        <a href={csvHref} data-testid={`${testIdPrefix}-csv-link`}>
          <Button size="sm" variant="outline" className="gap-1.5"><FileDown size={14} /> CSV</Button>
        </a>
      )}
    </div>
  );
}

function Table({ headers, rows, empty }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm pd-table">
        <thead>
          <tr className="text-left border-b border-[#EAEAE5] text-[10px] uppercase tracking-wider text-[#737A76]">
            {headers.map((h, i) => <th key={i} className="py-2 pr-3">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={headers.length} className="py-6 text-center text-[#737A76]">{empty || "No data"}</td></tr>
          ) : rows.map((r, i) => (
            <tr key={i} className="border-b border-[#F0F0EC] last:border-0">
              {r.map((c, j) => <td key={j} className="py-2.5 pr-3">{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================
// SECTION 1: Park Status snapshot
// ============================================================
function ParkStatusSection() {
  const { currentPark } = usePark();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!currentPark?.id) return;
    api.get("/reports/park-status").then((r) => setData(r.data)).catch(() => toast.error("Failed to load park status"));
  }, [currentPark?.id]);

  if (!data) return <div className="text-sm text-[#737A76] py-8">Loading…</div>;

  const c = data.contracts || {};
  const st = data.short_term_reservations || {};
  return (
    <div className="space-y-4" data-testid="park-status-section">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Stat label="Total lots" value={data.total_lots} sub={`${data.total_active_lots} rentable`} testId="ps-stat-total-lots" />
        <Stat label="Occupied" value={data.total_occupied} sub={`${data.occupancy_pct}% occupancy`} testId="ps-stat-occupied" />
        <Stat label="Check-ins today" value={data.check_ins_today} sub={`${data.check_ins_next_7} in next 7d`} testId="ps-stat-checkins" />
        <Stat label="Check-outs today" value={data.check_outs_today} testId="ps-stat-checkouts" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border-[#EAEAE5] shadow-none lg:col-span-2" data-testid="ps-lots-card">
          <CardContent className="p-5">
            <div className="text-[10px] uppercase tracking-widest text-[#737A76] mb-3">Lots by type</div>
            <Table
              headers={["Type", "Total", "Active", "Occupied", "Occupancy"]}
              rows={data.lots.map((l) => [
                l.lot_type,
                l.total,
                l.active,
                l.occupied,
                ["Storage", "Dumpster"].includes(l.lot_type)
                  ? <span className="text-[#737A76]">—</span>
                  : `${l.occupancy_pct}%`,
              ])}
              empty="No lots yet"
            />
          </CardContent>
        </Card>

        <Card className="border-[#EAEAE5] shadow-none" data-testid="ps-contracts-card">
          <CardContent className="p-5">
            <div className="text-[10px] uppercase tracking-widest text-[#737A76] mb-3">Active contracts</div>
            <Table
              headers={["Lease type", "Count"]}
              rows={[
                ["Long Term", c["Long Term"] || 0],
                ["Short Term", c["Short Term"] || 0],
                ["Sub Rental", c["Sub Rental"] || 0],
                ["Group", c["Group"] || 0],
                [<span key="renewals" className="text-[#737A76]">Renewals (≤30d)</span>, data.renewals_30d],
              ]}
            />
          </CardContent>
        </Card>
      </div>

      <Card className="border-[#EAEAE5] shadow-none" data-testid="ps-shortterm-card">
        <CardContent className="p-5">
          <div className="text-[10px] uppercase tracking-widest text-[#737A76] mb-3">Short-term reservations by check-in window</div>
          <div className="grid grid-cols-4 gap-4">
            <Stat label="Less than 30 days" value={st.less_than_30 || 0} />
            <Stat label="30 – 90 days" value={st.between_30_and_90 || 0} />
            <Stat label="Over 90 days" value={st.over_90 || 0} />
            <Stat label="Total" value={st.total || 0} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================
// SECTION 2: Operational reports
// ============================================================
function RentRollCard({ parkId }) {
  const [month, setMonth] = useState(ymIso());
  const [data, setData] = useState(null);
  const load = () => api.get(`/reports/rent-roll?month=${month}`).then((r) => setData(r.data)).catch(() => toast.error("Failed"));
  useEffect(() => { if (parkId) load(); /* eslint-disable-next-line */ }, [parkId, month]);
  const t = data?.totals || {};

  return (
    <Card className="border-[#EAEAE5] shadow-none" data-testid="report-rent-roll">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#737A76]">Rent Roll</div>
            <div className="text-lg font-medium tracking-tight" style={{ fontFamily: "Outfit" }}>Per-lot rent schedule</div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[#737A76]">Month</Label>
              <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40 h-9 bg-white border-[#EAEAE5]" data-testid="rent-roll-month" />
            </div>
            <ExportButtons
              pdfHref={exportUrl(`/reports/rent-roll.pdf?month=${month}`, parkId)}
              csvHref={exportUrl(`/reports/rent-roll.csv?month=${month}`, parkId)}
              testIdPrefix="rent-roll"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 pt-1">
          <Stat label="Lots" value={t.lots ?? 0} />
          <Stat label="Occupied" value={t.occupied ?? 0} />
          <Stat label="Vacant" value={t.vacant ?? 0} />
          <Stat label="Rent potential" value={formatCurrency(t.rent_potential)} />
          <Stat label="Balance due" value={formatCurrency(t.balance_due)} />
        </div>

        <Table
          headers={["Lot", "Type", "Status", "Tenant", "Lease", "Check In", "Check Out", "Rent", "Balance"]}
          rows={(data?.rows || []).map((r) => [
            r.lot_number, r.lot_type, r.status,
            r.tenant_name || "—", r.lease_type || "—",
            r.check_in || "—", r.check_out || "—",
            formatCurrency(r.rent), formatCurrency(r.balance),
          ])}
          empty="No data for this month"
        />
      </CardContent>
    </Card>
  );
}

function MonthlySummaryCard({ parkId }) {
  const [month, setMonth] = useState(ymIso());
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!parkId) return;
    api.get(`/reports/monthly-summary?month=${month}`).then((r) => setData(r.data)).catch(() => toast.error("Failed"));
  }, [parkId, month]);

  return (
    <Card className="border-[#EAEAE5] shadow-none" data-testid="report-monthly-summary">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#737A76]">Monthly Summary</div>
            <div className="text-lg font-medium tracking-tight" style={{ fontFamily: "Outfit" }}>Revenue, contracts, turnover</div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-[#737A76]">Month</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40 h-9 bg-white border-[#EAEAE5]" data-testid="monthly-summary-month" />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-1">
          <Stat label="Invoiced" value={formatCurrency(data?.invoiced_total)} sub={`${data?.new_invoices ?? 0} invoices`} />
          <Stat label="Paid" value={formatCurrency(data?.payment_total)} sub={`${data?.payments_received ?? 0} payments`} />
          <Stat label="New contracts" value={data?.new_contracts ?? 0} sub={`${data?.terminations ?? 0} terminations`} />
          <Stat label="Cancellations" value={data?.cancellations ?? 0} />
        </div>

        <div className="pt-2">
          <div className="text-[10px] uppercase tracking-widest text-[#737A76] mb-2">Payment methods</div>
          <Table
            headers={["Method", "Count", "Total"]}
            rows={(data?.payment_methods || []).map((m) => [m.method, m.count, formatCurrency(m.amount)])}
            empty="No payments this month"
          />
        </div>
      </CardContent>
    </Card>
  );
}

function RevenuesCard({ parkId }) {
  const [from, setFrom] = useState(daysAgoIso(29));
  const [to, setTo] = useState(todayIso());
  const [granularity, setGranularity] = useState("day");
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!parkId) return;
    api.get(`/reports/revenues?from=${from}&to=${to}&granularity=${granularity}`)
      .then((r) => setData(r.data)).catch(() => toast.error("Failed"));
  }, [parkId, from, to, granularity]);
  const max = useMemo(() => Math.max(1, ...(data?.series || []).map((s) => s.amount)), [data]);

  return (
    <Card className="border-[#EAEAE5] shadow-none" data-testid="report-revenues">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#737A76]">Revenues</div>
            <div className="text-lg font-medium tracking-tight" style={{ fontFamily: "Outfit" }}>Payments received over time</div>
          </div>
          <div className="flex items-end gap-3 flex-wrap">
            <div><Label className="text-[10px] uppercase text-[#737A76]">From</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40 h-9 bg-white border-[#EAEAE5]" /></div>
            <div><Label className="text-[10px] uppercase text-[#737A76]">To</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40 h-9 bg-white border-[#EAEAE5]" /></div>
            <div>
              <Label className="text-[10px] uppercase text-[#737A76]">Granularity</Label>
              <select value={granularity} onChange={(e) => setGranularity(e.target.value)} className="h-9 px-3 border border-[#EAEAE5] rounded-md bg-white text-sm" data-testid="revenues-granularity">
                <option value="day">Day</option><option value="week">Week</option><option value="month">Month</option>
              </select>
            </div>
          </div>
        </div>

        <div className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>
          {formatCurrency(data?.total)} <span className="text-xs text-[#737A76] font-normal">total</span>
        </div>

        <div className="grid grid-cols-[repeat(auto-fit,minmax(28px,1fr))] gap-1 items-end h-32 bg-[#FAFAF7] rounded-md p-3">
          {(data?.series || []).map((s, i) => (
            <div key={i} className="flex flex-col items-center group" title={`${s.period}: ${formatCurrency(s.amount)}`}>
              <div className="w-full bg-[#284B3B] rounded-sm transition-opacity hover:opacity-70"
                   style={{ height: `${(s.amount / max) * 100}%`, minHeight: s.amount > 0 ? 2 : 0 }} />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function DaysAvailableCard({ parkId }) {
  const [from, setFrom] = useState(todayIso());
  const [to, setTo] = useState(daysAgoIso(-29));
  const [data, setData] = useState(null);
  const load = () => api.get(`/reports/days-available?from=${from}&to=${to}`).then((r) => setData(r.data)).catch(() => toast.error("Failed"));
  useEffect(() => { if (parkId) load(); /* eslint-disable-next-line */ }, [parkId, from, to]);

  return (
    <Card className="border-[#EAEAE5] shadow-none" data-testid="report-days-available">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#737A76]">Days Available</div>
            <div className="text-lg font-medium tracking-tight" style={{ fontFamily: "Outfit" }}>Vacancy per lot over a date range</div>
          </div>
          <div className="flex items-end gap-3 flex-wrap">
            <div><Label className="text-[10px] uppercase text-[#737A76]">From</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40 h-9 bg-white border-[#EAEAE5]" /></div>
            <div><Label className="text-[10px] uppercase text-[#737A76]">To</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40 h-9 bg-white border-[#EAEAE5]" /></div>
            <ExportButtons
              pdfHref={exportUrl(`/reports/days-available.pdf?from=${from}&to=${to}`, parkId)}
              csvHref={exportUrl(`/reports/days-available.csv?from=${from}&to=${to}`, parkId)}
              testIdPrefix="days-available"
            />
          </div>
        </div>
        <Table
          headers={["Lot", "Type", "Total Days", "Occupied", "Available", "% Available"]}
          rows={(data?.rows || []).map((r) => [r.lot_number, r.lot_type, r.days_total, r.days_occupied, r.days_available, `${r.availability_pct}%`])}
          empty="No data"
        />
      </CardContent>
    </Card>
  );
}

function MarketingCard({ parkId }) {
  const [from, setFrom] = useState(daysAgoIso(89));
  const [to, setTo] = useState(todayIso());
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!parkId) return;
    api.get(`/reports/marketing?from=${from}&to=${to}`).then((r) => setData(r.data)).catch(() => toast.error("Failed"));
  }, [parkId, from, to]);

  return (
    <Card className="border-[#EAEAE5] shadow-none" data-testid="report-marketing">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#737A76]">Marketing — Lead Sources</div>
            <div className="text-lg font-medium tracking-tight" style={{ fontFamily: "Outfit" }}>Conversion + revenue by source</div>
          </div>
          <div className="flex items-end gap-3 flex-wrap">
            <div><Label className="text-[10px] uppercase text-[#737A76]">From</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40 h-9 bg-white border-[#EAEAE5]" /></div>
            <div><Label className="text-[10px] uppercase text-[#737A76]">To</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40 h-9 bg-white border-[#EAEAE5]" /></div>
            <ExportButtons
              pdfHref={exportUrl(`/reports/marketing.pdf?from=${from}&to=${to}`, parkId)}
              csvHref={exportUrl(`/reports/marketing.csv?from=${from}&to=${to}`, parkId)}
              testIdPrefix="marketing"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Stat label="Total leads" value={data?.total_leads ?? 0} />
          <Stat label="Revenue" value={formatCurrency(data?.total_revenue)} />
        </div>
        <Table
          headers={["Source", "Leads", "Active", "Cancelled", "Conversion", "Revenue"]}
          rows={(data?.rows || []).map((r) => [r.source, r.leads, r.active, r.cancelled, `${r.conversion_pct}%`, formatCurrency(r.revenue)])}
          empty="No data"
        />
      </CardContent>
    </Card>
  );
}

function TransfersCard({ parkId }) {
  const [from, setFrom] = useState(daysAgoIso(89));
  const [to, setTo] = useState(todayIso());
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!parkId) return;
    api.get(`/reports/transfers?from=${from}&to=${to}`).then((r) => setData(r.data)).catch(() => toast.error("Failed"));
  }, [parkId, from, to]);

  return (
    <Card className="border-[#EAEAE5] shadow-none" data-testid="report-transfers">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#737A76]">Lot Transfers</div>
            <div className="text-lg font-medium tracking-tight" style={{ fontFamily: "Outfit" }}>Tenants who moved between lots</div>
          </div>
          <div className="flex items-end gap-3 flex-wrap">
            <div><Label className="text-[10px] uppercase text-[#737A76]">From</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40 h-9 bg-white border-[#EAEAE5]" /></div>
            <div><Label className="text-[10px] uppercase text-[#737A76]">To</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40 h-9 bg-white border-[#EAEAE5]" /></div>
            <ExportButtons
              pdfHref={exportUrl(`/reports/transfers.pdf?from=${from}&to=${to}`, parkId)}
              csvHref={exportUrl(`/reports/transfers.csv?from=${from}&to=${to}`, parkId)}
              testIdPrefix="transfers"
            />
          </div>
        </div>
        <Table
          headers={["Tenant", "From Lot", "Out", "To Lot", "In"]}
          rows={(data?.rows || []).map((r) => [r.tenant_name, r.from_lot, formatDate(r.from_check_out), r.to_lot, formatDate(r.to_check_in)])}
          empty="No transfers in range"
        />
      </CardContent>
    </Card>
  );
}

function AgingCard() {
  const [data, setData] = useState([]);
  const { currentPark } = usePark();
  useEffect(() => {
    if (!currentPark?.id) return;
    api.get("/reports/aging").then((r) => setData(r.data)).catch(() => toast.error("Failed"));
  }, [currentPark?.id]);
  const totalBal = data.reduce((s, r) => s + (r.balance || 0), 0);
  return (
    <Card className="border-[#EAEAE5] shadow-none" data-testid="report-aging">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#737A76]">Aging</div>
            <div className="text-lg font-medium tracking-tight" style={{ fontFamily: "Outfit" }}>Tenants with open balances</div>
          </div>
          <div className="text-sm text-[#A13E34] font-medium tabular-nums" data-testid="aging-total">{formatCurrency(totalBal)} outstanding</div>
        </div>
        <Table
          headers={["Tenant", "Open invoices", "Balance"]}
          rows={data.map((a) => [a.tenant_name, a.invoice_count, formatCurrency(a.balance)])}
          empty="No outstanding balances"
        />
      </CardContent>
    </Card>
  );
}

// ============================================================
// SECTION 3: Directories
// ============================================================
function DirectoryCard({ title, subtitle, path, headers, mapRow, parkId, testId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!parkId) return;
    api.get(path).then((r) => setData(r.data)).catch(() => toast.error("Failed"));
  }, [parkId, path]);
  return (
    <Card className="border-[#EAEAE5] shadow-none" data-testid={testId}>
      <CardContent className="p-5 space-y-3">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#737A76]">{title}</div>
            <div className="text-lg font-medium tracking-tight" style={{ fontFamily: "Outfit" }}>{subtitle}</div>
            <div className="text-xs text-[#737A76] mt-1">{data?.total ?? 0} entries</div>
          </div>
          <ExportButtons
            pdfHref={exportUrl(`${path}.pdf`.replace(/(\?.*)?$/, ".pdf"), parkId)}
            csvHref={exportUrl(`${path}.csv`.replace(/(\?.*)?$/, ".csv"), parkId)}
            testIdPrefix={testId}
          />
        </div>
        <Table headers={headers} rows={(data?.rows || []).map(mapRow)} empty="No data" />
      </CardContent>
    </Card>
  );
}

// ============================================================
// Page
// ============================================================
export default function ReportsPage() {
  const { currentPark } = usePark();
  const parkId = currentPark?.id;

  return (
    <div data-testid="reports-page">
      <PageHeader title="Reports" subtitle="Park status, operational reports, and printable directories" />

      <Tabs defaultValue="status" className="mt-2">
        <TabsList className="bg-[#F0F0EC]">
          <TabsTrigger value="status" data-testid="tab-status">Park Status</TabsTrigger>
          <TabsTrigger value="rent-roll" data-testid="tab-rent-roll">Rent Roll</TabsTrigger>
          <TabsTrigger value="monthly" data-testid="tab-monthly">Monthly</TabsTrigger>
          <TabsTrigger value="revenues" data-testid="tab-revenues">Revenues</TabsTrigger>
          <TabsTrigger value="aging" data-testid="tab-aging">Aging</TabsTrigger>
          <TabsTrigger value="availability" data-testid="tab-availability">Availability</TabsTrigger>
          <TabsTrigger value="marketing" data-testid="tab-marketing">Marketing</TabsTrigger>
          <TabsTrigger value="transfers" data-testid="tab-transfers">Transfers</TabsTrigger>
          <TabsTrigger value="directories" data-testid="tab-directories">Directories</TabsTrigger>
        </TabsList>

        <TabsContent value="status" className="mt-4"><ParkStatusSection /></TabsContent>
        <TabsContent value="rent-roll" className="mt-4"><RentRollCard parkId={parkId} /></TabsContent>
        <TabsContent value="monthly" className="mt-4"><MonthlySummaryCard parkId={parkId} /></TabsContent>
        <TabsContent value="revenues" className="mt-4"><RevenuesCard parkId={parkId} /></TabsContent>
        <TabsContent value="aging" className="mt-4"><AgingCard /></TabsContent>
        <TabsContent value="availability" className="mt-4"><DaysAvailableCard parkId={parkId} /></TabsContent>
        <TabsContent value="marketing" className="mt-4"><MarketingCard parkId={parkId} /></TabsContent>
        <TabsContent value="transfers" className="mt-4"><TransfersCard parkId={parkId} /></TabsContent>
        <TabsContent value="directories" className="mt-4 space-y-4">
          <DirectoryCard
            title="Resident Directory"
            subtitle="Community-safe (no email/phone/address)"
            path="/directories/residents"
            headers={["Name", "Lot", "Type", "Lease", "Since", "Emergency Contact", "Pets"]}
            mapRow={(r) => [r.name, r.lot, r.lot_type, r.lease_type, r.since, r.emergency_contact, r.pets]}
            parkId={parkId}
            testId="dir-residents"
          />
          <DirectoryCard
            title="Lot # Directory"
            subtitle="All lots sorted by lot number"
            path="/directories/lots-by-number"
            headers={["Lot", "Type", "Status", "Occupant", "Lease Until"]}
            mapRow={(r) => [r.lot_number, r.lot_type, r.active ? "Active" : "Inactive", r.occupant, r.lease_until]}
            parkId={parkId}
            testId="dir-lots-by-number"
          />
          <DirectoryCard
            title="Park Lots Inventory"
            subtitle="Rates + amenities for every lot"
            path="/directories/park-lots"
            headers={["Lot", "Type", "Size", "Pad", "Rent", "Status", "Amenities"]}
            mapRow={(r) => [r.lot_number, r.lot_type, r.size, r.pad_size, formatCurrency(r.monthly_rent), r.active ? "Active" : "Inactive", r.amenities]}
            parkId={parkId}
            testId="dir-park-lots"
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
